import styled from 'astroturf/react';
import React, { ReactNode } from 'react';

import { FollowableUser } from 'js/social/shared/types';
import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { AuthorHeader } from './AuthorHeader';
import { ImpressionPropsGenerator } from 'js/social/ImpressionHelper';
import _Wrapper from "NewsHeader-Wrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/News/NewsHeader.tsx?styleId=Wrapper";
import _SmallAvatar from "NewsHeader-SmallAvatar.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/News/NewsHeader.tsx?styleId=SmallAvatar";
import _AvatarLink from "NewsHeader-AvatarLink.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/News/NewsHeader.tsx?styleId=AvatarLink";
import _ReasonWrapper from "NewsHeader-ReasonWrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/News/NewsHeader.tsx?styleId=ReasonWrapper";
import _ReasonText from "NewsHeader-ReasonText.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/News/NewsHeader.tsx?styleId=ReasonText";
import _ReasonLink from "NewsHeader-ReasonLink.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/News/NewsHeader.tsx?styleId=ReasonLink";
import _RelatedUserInfo from "NewsHeader-RelatedUserInfo.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/News/NewsHeader.tsx?styleId=RelatedUserInfo";
import _PostName from "NewsHeader-PostName.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/News/NewsHeader.tsx?styleId=PostName";


const Wrapper = /*#__PURE__*/styled("div", null, {
  displayName: "Wrapper",
  styles: _Wrapper
});

const SmallAvatar = /*#__PURE__*/styled("img", null, {
  displayName: "SmallAvatar",
  styles: _SmallAvatar
});

const AvatarLink = /*#__PURE__*/styled("a", null, {
  displayName: "AvatarLink",
  styles: _AvatarLink
});

const ReasonWrapper = /*#__PURE__*/styled("div", null, {
  displayName: "ReasonWrapper",
  styles: _ReasonWrapper
});

const ReasonText = /*#__PURE__*/styled(Body, null, {
  displayName: "ReasonText",
  styles: _ReasonText
});

const ReasonLink = /*#__PURE__*/styled(BodyLink, null, {
  displayName: "ReasonLink",
  styles: _ReasonLink
});

const RelatedUserInfo = /*#__PURE__*/styled("div", null, {
  displayName: "RelatedUserInfo",
  styles: _RelatedUserInfo
});

const PostName = /*#__PURE__*/styled(Body, null, {
  displayName: "PostName",
  styles: _PostName
});

const AutoGroupReason = ({
  reason,
  autoGroup,
}: {
  reason: Reason;
  autoGroup: string;
}) => {
  if (reason === 'new_paper_in_auto_group') {
    return (
      <ReasonWrapper>
        <ReasonText size="sm">New paper in {autoGroup}</ReasonText>
      </ReasonWrapper>
    );
  } else if (reason === 'auto_group_comment') {
    return (
      <ReasonWrapper>
        <ReasonText size="sm">New comment in {autoGroup}</ReasonText>
      </ReasonWrapper>
    );
  }
  return null;
};

const RelatedUserReason = ({
  reason,
  relatedUser,
}: {
  reason: Reason;
  relatedUser: FollowableUser;
}) => {
  if (reason === 'user_liked') {
    return (
      <ReasonWrapper>
        <RelatedUserInfo>
          <AvatarLink href={relatedUser.url}>
            <SmallAvatar src={relatedUser.photo} />
          </AvatarLink>
          {/* spacer */}
          <div />
          <ReasonLink size="sm" bold href={relatedUser.url}>
            {relatedUser.displayName}
          </ReasonLink>
          <Body size="sm">liked</Body>
        </RelatedUserInfo>
      </ReasonWrapper>
    );
  } else if (reason === 'user_commented') {
    return (
      <ReasonWrapper>
        <RelatedUserInfo>
          <AvatarLink href={relatedUser.url}>
            <SmallAvatar src={relatedUser.photo} />
          </AvatarLink>
          {/* spacer */}
          <div />
          <ReasonLink size="sm" bold href={relatedUser.url}>
            {relatedUser.displayName}
          </ReasonLink>
          <Body size="sm">commented</Body>
        </RelatedUserInfo>
      </ReasonWrapper>
    );
  }
  return null;
};

type Reason =
  | 'new_paper_in_auto_group'
  | 'user_liked'
  | 'user_commented'
  | 'auto_group_comment';

type AutoGroupV1Props = {
  type: 'auto_group_recommended' | 'user_post';
  reason: Reason | null;
  authorUser: FollowableUser | null;
  relatedUser: FollowableUser | null;
  autoGroup: string | null;
};

type AutoGroupV2Props = {
  type: 'autogroup_multi_user_recommended';
  relevantUsers: FollowableUser[];
  autoGroupTitle?: string;
  action: 'save' | 'view';
  tooltipContents: ReactNode;
};

type RecommendationProps = {
  type: 'recommended';
};

type PostProps = {
  type: 'post';
  author: FollowableUser;
};

type SharedProps = {
  impressionPropsGen: ImpressionPropsGenerator;
  timestamp: string;
  variant?: 'default' | 'dense';
};

type Props = SharedProps &
  (AutoGroupV1Props | AutoGroupV2Props | RecommendationProps | PostProps);

export const NewsHeader = (props: Props) => {
  const { type, timestamp, impressionPropsGen, variant = 'default' } = props;

  if (type === 'post') {
    const { author } = props;
    return (
      <Wrapper variant={variant}>
        <AuthorHeader
          kind="user"
          user={{ ...author, displayName: author.firstName }}
          size="md"
          showAvatar
          showTimestamp
          impressionPropsGen={impressionPropsGen}
          variant={variant}
        />
      </Wrapper>
    );
  } else if (type === 'user_post' || type === 'auto_group_recommended') {
    const { reason, authorUser, relatedUser, autoGroup } = props;

    const headerReason = (
      <>
        {reason === 'new_paper_in_auto_group' && autoGroup && (
          <AutoGroupReason reason={reason} autoGroup={autoGroup} />
        )}
        {reason === 'user_liked' && relatedUser && (
          <RelatedUserReason reason={reason} relatedUser={relatedUser} />
        )}
        {reason === 'user_commented' && relatedUser && (
          <RelatedUserReason reason={reason} relatedUser={relatedUser} />
        )}
        {reason === 'auto_group_comment' && autoGroup && (
          <AutoGroupReason reason={reason} autoGroup={autoGroup} />
        )}
      </>
    );

    if (type === 'user_post' && authorUser) {
      return (
        <Wrapper variant={variant}>
          {headerReason}
          <AuthorHeader
            kind="user"
            user={authorUser}
            size="md"
            showAvatar
            showTimestamp
            timestamp={timestamp}
            impressionPropsGen={impressionPropsGen}
            variant={variant}
          />
        </Wrapper>
      );
    } else if (type === 'auto_group_recommended' && autoGroup) {
      return variant === 'default' ? (
        <Wrapper variant={variant}>
          {headerReason}
          <AuthorHeader
            kind="recommendation"
            recommendation={{
              name: `Topic: ${autoGroup}`,
              subtitle: 'Recommended based on your interests',
            }}
            size="md"
            showAvatar
            impressionPropsGen={impressionPropsGen}
            variant={variant}
          />
        </Wrapper>
      ) : (
        <Wrapper variant={variant}>
          {headerReason}
          <PostName size="md">Recommended paper</PostName>
        </Wrapper>
      );
    } else {
      return null;
    }
  } else if (type === 'autogroup_multi_user_recommended') {
    const { relevantUsers, autoGroupTitle, action, tooltipContents } = props;

    return (
      <Wrapper variant={variant}>
        <AuthorHeader
          kind="multi-user"
          users={relevantUsers}
          userTitle="published"
          groupName={autoGroupTitle}
          action={action}
          paperType="paper"
          showAvatar
          size="md"
          avatarInteractionContents={tooltipContents}
          impressionPropsGen={impressionPropsGen}
          variant={variant}
        />
      </Wrapper>
    );
  } else if (type === 'recommended') {
    return variant === 'default' ? (
      <Wrapper variant={variant}>
        <AuthorHeader
          kind="recommendation"
          recommendation={{
            name: 'Academia Paper Recommendations',
            subtitle: 'Recommended based on your interests',
          }}
          size="md"
          showAvatar={true}
          impressionPropsGen={impressionPropsGen}
          variant={variant}
        />
      </Wrapper>
    ) : null;
  } else {
    return null;
  }
};
